// ==========================================
// src/pages/ContratoCarga.jsx
// CONTRATO DE CARGA - V6 DEFINITIVO CORRIGIDO
// ✅ motorista_id = ID da tabela motoristas (não auth.uid)
// ✅ Busca motorista do Supabase antes de criar contrato
// ✅ Status 'aceito' compatível com CHECK constraint
// ✅ Salva contrato no SUPABASE + localStorage
// ==========================================

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  ArrowLeft, FileText, CheckCircle2, Loader2, 
  Printer, Download, MapPin, Truck, Building2, 
  CreditCard, Phone, Mail, Lock, AlertTriangle, ShieldAlert,
  MessageCircle, Weight, DollarSign, Route
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { supabase } from '@/lib/supabase';

export default function ContratoCarga() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const contratoRef = useRef(null);

  const [carga, setCarga] = useState(null);
  const [motorista, setMotorista] = useState(null);
  const [motoristaSupabase, setMotoristaSupabase] = useState(null); // ✅ NOVO: dados do Supabase
  const [empresa, setEmpresa] = useState(null);
  const [aceito, setAceito] = useState(false);
  const [loading, setLoading] = useState(true);
  const [assinando, setAssinando] = useState(false);
  const [contratoGerado, setContratoGerado] = useState(false);
  const [numeroContrato, setNumeroContrato] = useState('');
  const [checklist, setChecklist] = useState({
    termos: false
  });

  const [liberado, setLiberado] = useState(false);
  const [verificando, setVerificando] = useState(true);
  const [erroLiberacao, setErroLiberacao] = useState(null);

  const atualizarCargaLocalStorage = (cargaId, pagamentoConfirmado = false) => {
    try {
      const todasCargas = JSON.parse(localStorage.getItem('borafrete_cargas') || '[]');
      const indexCarga = todasCargas.findIndex(c => c.id === cargaId);

      if (indexCarga >= 0) {
        todasCargas[indexCarga].status = 'aceita';
        todasCargas[indexCarga].etapa = 'contrato_assinado';
        todasCargas[indexCarga].contrato_liberado = true;
        todasCargas[indexCarga].contrato_liberado_em = new Date().toISOString();

        if (pagamentoConfirmado) {
          todasCargas[indexCarga].pagamento_confirmado = true;
          todasCargas[indexCarga].data_pagamento = new Date().toISOString();
        }

        localStorage.setItem('borafrete_cargas', JSON.stringify(todasCargas));
        return todasCargas[indexCarga];
      }
      return null;
    } catch (error) {
      console.error('[ContratoCarga] Erro ao atualizar localStorage:', error);
      return null;
    }
  };

  useEffect(() => {
    const carregarDados = async () => {
      try {
        setVerificando(true);

        const veioDoPagamento = location.state?.fromPagamento === true || 
                               location.state?.paymentConfirmed === true ||
                               sessionStorage.getItem('pagamento_confirmado') === 'true';

        if (veioDoPagamento) {
          atualizarCargaLocalStorage(id, true);
          sessionStorage.removeItem('pagamento_confirmado');
        }

        const liberacaoStatus = await verificarLiberacaoContrato(id);

        if (!liberacaoStatus.liberado) {
          setErroLiberacao(liberacaoStatus.mensagem);
          setVerificando(false);
          setLoading(false);
          return;
        }

        setLiberado(true);
        setCarga(liberacaoStatus.carga);

        const cargaData = liberacaoStatus.carga;
        const empresaData = {
          razao_social: cargaData.empresa_razao_social || cargaData.empresa_nome || 'Empresa Contratante',
          cnpj: cargaData.empresa_cnpj || 'Não informado',
          telefone: cargaData.empresa_telefone || cargaData.telefone_contato || 'Não informado',
          email: cargaData.empresa_email || cargaData.email_contato || 'Não informado',
        };

        setEmpresa(empresaData);

        // ✅ CORREÇÃO: Buscar motorista do Supabase com ID correto
        let motoristaData = null;
        let motoristaSupabaseData = null;

        if (user?.id) {
          try {
            const { data: ms, error } = await supabase
              .from('motoristas')
              .select('id, nome_completo, cpf, telefone, email, cnh, placa_caminhao, categoria_cnh')
              .eq('usuario_id', user.id)
              .maybeSingle();

            if (!error && ms) {
              motoristaSupabaseData = ms;
              setMotoristaSupabase(ms);
              
              motoristaData = {
                id: ms.id, // ✅ ID da tabela motoristas (UUID correto)
                nome: ms.nome_completo || user.nome || 'Informar',
                email: ms.email || user.email || 'Não informado',
                cpf: ms.cpf || 'Não informado',
                telefone: ms.telefone || 'Não informado',
                cnh: ms.cnh || 'Não informado',
                placa: ms.placa_caminhao || 'Informar',
                tipoVeiculo: ms.categoria_cnh || cargaData?.tipo_veiculo || 'Truck',
              };
              console.log('[ContratoCarga] Motorista Supabase encontrado:', ms.id);
            }
          } catch (err) {
            console.warn('[ContratoCarga] Erro ao buscar motorista:', err);
          }
        }

        // Fallback localStorage
        if (!motoristaData) {
          const userData = JSON.parse(localStorage.getItem('borafrete_user') || '{}');
          const motoristas = JSON.parse(localStorage.getItem('borafrete_motoristas') || '[]');
          const motoristaCompleto = motoristas.find(m => m.email === (user?.email || userData?.email));

          motoristaData = {
            id: motoristaCompleto?.id || user?.id, // ⚠️ Pode ser auth.uid se não achou no Supabase
            nome: user?.nome || userData?.nome || motoristaCompleto?.nome || 'Informar',
            email: user?.email || userData?.email || motoristaCompleto?.email || 'Não informado',
            cpf: user?.cpf || userData?.cpf || motoristaCompleto?.cpf || 'Não informado',
            telefone: user?.telefone || userData?.telefone || motoristaCompleto?.telefone || 'Não informado',
            cnh: user?.cnh || userData?.cnh || motoristaCompleto?.cnh || 'Não informado',
            placa: user?.placa_veiculo || userData?.placa_veiculo || motoristaCompleto?.placa_veiculo || 'Informar',
            tipoVeiculo: user?.tipo_veiculo || userData?.tipo_veiculo || motoristaCompleto?.tipo_veiculo || cargaData?.tipo_veiculo || 'Truck',
          };
        }

        setMotorista(motoristaData);

        const dataAtual = new Date();
        const numero = `BF-${dataAtual.getFullYear()}${String(dataAtual.getMonth() + 1).padStart(2, '0')}${String(dataAtual.getDate()).padStart(2, '0')}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`;
        setNumeroContrato(numero);

        const contratosExistentes = JSON.parse(localStorage.getItem('borafrete_contratos') || '[]');
        const contratoExistente = contratosExistentes.find(c => c.carga_id === id && c.motorista?.email === motoristaData.email);

        if (contratoExistente) {
          setContratoGerado(true);
          setNumeroContrato(contratoExistente.numeroContrato);
          toast.info('Você já possui um contrato para esta carga!');
        }

        setLoading(false);
        setVerificando(false);

      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        toast.error('Erro ao carregar dados do contrato');
        setErroLiberacao('Erro ao carregar contrato. Tente novamente.');
        setLoading(false);
        setVerificando(false);
      }
    };

    carregarDados();
  }, [id, location.state, navigate, user]);

  const verificarLiberacaoContrato = async (cargaId) => {
    try {
      if (location.state?.fromPagamento === true || location.state?.paymentConfirmed === true) {
        const todasCargas = JSON.parse(localStorage.getItem('borafrete_cargas') || '[]');
        const carga = todasCargas.find(c => c.id === cargaId);
        if (carga) {
          carga.status = 'aceita';
          carga.etapa = 'contrato_assinado';
          carga.contrato_liberado = true;
          carga.contrato_liberado_em = new Date().toISOString();
          localStorage.setItem('borafrete_cargas', JSON.stringify(todasCargas));
          return { liberado: true, carga };
        }
      }

      const todasCargas = JSON.parse(localStorage.getItem('borafrete_cargas') || '[]');
      const carga = todasCargas.find(c => c.id === cargaId);

      if (!carga) {
        try {
          const { data: cargaSupabase, error } = await supabase
            .from('cargas')
            .select('id,titulo,status,aprovada_admin,motorista_id,empresa_id,empresa_nome,empresa_razao_social,empresa_cnpj,empresa_telefone,empresa_email,origem_cidade,origem_estado,origem_endereco,destino_cidade,destino_estado,destino_endereco,data_coleta,hora_coleta,data_entrega,hora_entrega,tipo_veiculo,tipo_carga,peso,peso_unidade,peso_valor,valor_frete,valor_total,metros_cubicos,tipo_valor,created_at')
            .eq('id', cargaId)
            .maybeSingle();

          if (!error && cargaSupabase) {
            if (cargaSupabase.status === 'aceita' || cargaSupabase.motorista_id) {
              return { liberado: true, carga: cargaSupabase };
            }
            return { liberado: false, mensagem: 'Carga ainda não foi aceita.', carga: cargaSupabase };
          }
        } catch (err) {
          console.warn('Erro Supabase:', err);
        }
        return { liberado: false, mensagem: 'Carga não encontrada.' };
      }

      const statusLiberados = ['paga', 'liberada', 'aprovada', 'contrato', 'aceita'];
      const etapaLiberada = ['contrato', 'pagamento', 'liberada', 'contrato_assinado'];

      if (statusLiberados.includes(carga.status) && etapaLiberada.includes(carga.etapa)) {
        return { liberado: true, carga };
      }

      if (carga.contrato_liberado === true || carga.pagamento_confirmado === true) {
        return { liberado: true, carga };
      }

      if (carga.motorista_id && carga.motorista_id === user?.id) {
        return { liberado: true, carga };
      }

      return { 
        liberado: false, 
        mensagem: 'Contrato ainda não liberado. Aguardando confirmação do pagamento.',
        carga 
      };

    } catch (error) {
      return { 
        liberado: false, 
        mensagem: 'Erro ao verificar liberação.' 
      };
    }
  };

  const formatarQuantidade = (carga) => {
    if (!carga) return '--';
    if (carga.peso_valor && carga.peso_unidade) {
      const valor = parseFloat(carga.peso_valor) || 0;
      if (valor === 0) return '--';
      return carga.peso_unidade === 'ton' ? `${valor.toLocaleString('pt-BR')} T` : `${valor} ${carga.peso_unidade}`;
    }
    if (carga.peso) {
      const pesoNum = parseFloat(carga.peso);
      if (!isNaN(pesoNum) && pesoNum > 0) return `${pesoNum.toLocaleString('pt-BR')} T`;
    }
    return '--';
  };

  const handleImprimir = () => {
    window.print();
    toast.success('Contrato enviado para impressão');
  };

  const handleSalvarPDF = () => {
    toast.success('Contrato salvo como PDF!');
    setTimeout(() => window.print(), 500);
  };

  const handleChecklistChange = (item) => {
    setChecklist(prev => ({ ...prev, [item]: !prev[item] }));
  };

  const handleAbrirWhatsApp = () => {
    const url = `https://wa.me/5565981181997?text=${encodeURIComponent('Olá! Preciso de assistência com seguro de carga.')}`;
    window.open(url, '_blank');
  };

  // ============================================================
  // ✅ FUNÇÃO CORRIGIDA: motorista_id = ID da tabela motoristas
  // ============================================================
  const handleAceitar = async () => {
    if (!aceito) {
      toast.error('Você precisa aceitar os termos do contrato');
      return;
    }

    if (!checklist.termos) {
      toast.error('Você precisa confirmar a declaração de responsabilidade');
      return;
    }

    if (!user?.id) {
      toast.error('Erro: Usuário não identificado. Faça login novamente.');
      return;
    }

    // ✅ CORREÇÃO: Verificar se temos o motorista_id correto do Supabase
    const motoristaIdReal = motoristaSupabase?.id || motorista?.id;
    
    if (!motoristaIdReal) {
      toast.error('Erro: ID do motorista não encontrado. Faça login novamente.');
      return;
    }

    console.log('[ContratoCarga] Usando motorista_id:', motoristaIdReal);

    setAssinando(true);

    try {
      const agora = new Date().toISOString();
      const dataVencimento = carga.data_entrega || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

      // ============================================================
      // ✅ PASSO 1: Criar contrato no SUPABASE com motorista_id correto
      // ============================================================
      let contratoSupabaseId = null;

      try {
        const { data: novoContrato, error: erroContrato } = await supabase
          .from('contratos')
          .insert({
            motorista_id: motoristaIdReal,        // ✅ CORRETO! ID da tabela motoristas
            carga_id: id,
            numero_contrato: numeroContrato,
            status: 'aceito',                     // ✅ 'aceito' existe no CHECK
            data_aceite: agora,
            data_inicio: agora,
            data_vencimento: dataVencimento,
            valor_frete: carga.valor_frete || carga.valor_total || 0,
            taxa_agenciamento: 50.00,
            motorista_nome: motorista.nome,
            motorista_email: motorista.email,
            motorista_cnh: motorista.cnh,
            empresa_id: carga.empresa_id,
            empresa_nome: empresa.razao_social,
            empresa_cnpj: empresa.cnpj,
            empresa_telefone: empresa.telefone,
            origem_cidade: carga.origem_cidade,
            origem_estado: carga.origem_estado,
            destino_cidade: carga.destino_cidade,
            destino_estado: carga.destino_estado,
            tipo_veiculo: carga.tipo_veiculo,
            tipo_carga: carga.tipo_carga,
            peso_valor: carga.peso_valor,
            peso_unidade: carga.peso_unidade
          })
          .select('id')
          .single();

        if (erroContrato) {
          console.warn('[ContratoCarga] Erro ao salvar no Supabase:', erroContrato.message);
          toast.warning('Contrato salvo localmente. Sincronização com servidor pendente.');
        } else {
          contratoSupabaseId = novoContrato?.id;
          console.log('[ContratoCarga] Contrato salvo no Supabase:', contratoSupabaseId);
          toast.success('Contrato registrado no sistema!');
        }
      } catch (err) {
        console.warn('[ContratoCarga] Falha Supabase:', err);
        toast.warning('Salvando contrato localmente...');
      }

      // ============================================================
      // ✅ PASSO 2: Atualizar carga com motorista_id correto no SUPABASE
      // ============================================================
      try {
        const { error: erroCarga } = await supabase
          .from('cargas')
          .update({
            status: 'aceita',
            motorista_id: motoristaIdReal,        // ✅ ID correto da tabela motoristas
            motorista_nome: motorista.nome,
            motorista_email: motorista.email,
            motorista_telefone: motorista.telefone,
            data_aceite: agora,
            contrato_numero: numeroContrato,
            contrato_id: contratoSupabaseId
          })
          .eq('id', id);

        if (erroCarga) {
          console.warn('[ContratoCarga] Erro ao atualizar carga:', erroCarga.message);
        } else {
          console.log('[ContratoCarga] Carga atualizada no Supabase');
        }
      } catch (err) {
        console.warn('[ContratoCarga] Falha ao atualizar carga:', err);
      }

      // ============================================================
      // ✅ PASSO 3: Salvar no localStorage (backup/fallback)
      // ============================================================
      const contratoData = {
        id: contratoSupabaseId || `CTR-${Date.now()}`,
        numeroContrato,
        carga_id: id,
        dataContrato: agora,
        dataAceite: agora,
        data_inicio: agora,
        data_vencimento: dataVencimento,
        status: 'aceito',
        etapa: 'iniciada',
        ativo: true,
        motorista: { 
          ...motorista,
          userId: user?.id,
        },
        motorista_id: motoristaIdReal,            // ✅ ID correto
        motorista_email: motorista.email,
        motorista_nome: motorista.nome,
        empresa: { ...empresa },
        empresa_id: carga.empresa_id,
        origem: carga.origem_cidade,
        destino: carga.destino_cidade,
        valor: carga.valor || carga.valor_frete || 0,
        valor_frete: carga.valor_frete || carga.valor || 0,
        peso: carga.peso_valor || carga.peso,
        tipo_veiculo: carga.tipo_veiculo,
        tipo_carga: carga.tipo_carga,
        produto: carga.titulo || carga.tipo_carga || 'Carga',
        financeiro: {
          taxaAgenciamento: 50.00,
          pago: true,
          dataPagamento: agora,
          metodoPagamento: 'PIX'
        },
        aceito: true,
        checklistConfirmado: checklist,
      };

      const contratosExistentes = JSON.parse(localStorage.getItem('borafrete_contratos') || '[]');
      const indexExistente = contratosExistentes.findIndex(c => c.carga_id === id && c.motorista_email === motorista.email);

      if (indexExistente >= 0) {
        contratosExistentes[indexExistente] = contratoData;
      } else {
        contratosExistentes.push(contratoData);
      }

      localStorage.setItem('borafrete_contratos', JSON.stringify(contratosExistentes));

      // Atualizar carga no localStorage também
      const todasCargas = JSON.parse(localStorage.getItem('borafrete_cargas') || '[]');
      const indexCarga = todasCargas.findIndex(c => c.id === id);

      if (indexCarga >= 0) {
        todasCargas[indexCarga].status = 'aceita';
        todasCargas[indexCarga].etapa = 'iniciada';
        todasCargas[indexCarga].contrato_id = numeroContrato;
        todasCargas[indexCarga].motorista_id = motoristaIdReal; // ✅ ID correto
        localStorage.setItem('borafrete_cargas', JSON.stringify(todasCargas));
      }

      // ============================================================
      // ✅ PASSO 4: Redirecionar para agradecimento
      // ============================================================
      navigate('/agradecimento', {
        state: {
          numeroContrato,
          carga: {
            id: carga.id,
            titulo: carga.titulo,
            origem: carga.origem_cidade,
            destino: carga.destino_cidade,
            empresa_id: carga.empresa_id,
            valor_frete: carga.valor_frete || carga.valor_total || 0
          },
          motorista: motorista.nome,
          motorista_id: motoristaIdReal,          // ✅ Passar ID correto para Agradecimento
          motorista_email: motorista.email,
          motorista_cnh: motorista.cnh
        }
      });

    } catch (error) {
      console.error('Erro ao aceitar contrato:', error);
      toast.error('Erro ao salvar contrato. Tente novamente.');
    } finally {
      setAssinando(false);
    }
  };

  if (verificando) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="w-12 h-12 animate-spin text-green-600 mb-4" />
        <p className="text-slate-600 font-medium">Verificando liberação do contrato...</p>
      </div>
    );
  }

  if (!liberado || erroLiberacao) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <Lock className="w-10 h-10 text-amber-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Contrato Bloqueado</h2>
          <p className="text-slate-600 mb-6">{erroLiberacao || 'Aguardando liberação.'}</p>
          <Button variant="outline" onClick={() => navigate('/buscar-cargas')} className="w-full">
            Buscar Outras Cargas
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (!carga) return null;

  const dataAtual = new Date();
  const quantidadeFormatada = formatarQuantidade(carga);

  return (
    <div className="min-h-screen bg-slate-50 pb-20 print:bg-white print:pb-0">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 print:hidden">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-600">
            <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
          </Button>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-600" />
            <span className="font-semibold text-slate-800">Contrato de Carga</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleImprimir}>
              <Printer className="w-4 h-4 mr-2" /> Imprimir
            </Button>
            <Button variant="outline" size="sm" onClick={handleSalvarPDF}>
              <Download className="w-4 h-4 mr-2" /> PDF
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8" ref={contratoRef}>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 mb-6 print:shadow-none print:border-black">

          {/* Cabeçalho */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-slate-900 mb-2">CONTRATO DE TRANSPORTE DE CARGA</h1>
            <p className="text-sm text-slate-500">Nº {numeroContrato}</p>
            <p className="text-sm text-slate-500">{format(dataAtual, 'dd/MM/yyyy', { locale: ptBR })}</p>
          </div>

          {/* Partes */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> CONTRATANTE (Empresa)
              </h3>
              <p className="text-sm"><strong>Razão Social:</strong> {empresa.razao_social}</p>
              <p className="text-sm"><strong>CNPJ:</strong> {empresa.cnpj}</p>
              <p className="text-sm"><strong>Telefone:</strong> {empresa.telefone}</p>
              <p className="text-sm"><strong>Email:</strong> {empresa.email}</p>
            </div>

            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <Truck className="w-4 h-4" /> CONTRATADO (Motorista)
              </h3>
              <p className="text-sm"><strong>Nome:</strong> {motorista.nome}</p>
              <p className="text-sm"><strong>CPF:</strong> {motorista.cpf}</p>
              <p className="text-sm"><strong>Telefone:</strong> {motorista.telefone}</p>
              <p className="text-sm"><strong>Email:</strong> {motorista.email}</p>
              <p className="text-sm"><strong>CNH:</strong> {motorista.cnh}</p>
              <p className="text-sm"><strong>Placa:</strong> {motorista.placa}</p>
              <p className="text-sm"><strong>Veículo:</strong> {motorista.tipoVeiculo}</p>
            </div>
          </div>

          {/* Objeto */}
          <div className="mb-8">
            <h3 className="font-semibold text-slate-900 mb-4 text-lg">1. OBJETO DO CONTRATO</h3>
            <p className="text-sm text-slate-700 leading-relaxed mb-4">
              O presente contrato tem como objeto o transporte de carga descrita abaixo, 
              de acordo com as especificações e condições estabelecidas neste documento.
            </p>

            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-slate-900 mb-1">Descrição</p>
                  <p className="text-sm text-slate-700">{carga.titulo || 'Carga'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 mb-1">Tipo</p>
                  <p className="text-sm text-slate-700">{carga.tipo_carga || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 mb-1">Quantidade</p>
                  <p className="text-sm text-slate-700">{quantidadeFormatada}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 mb-1">Veículo Requerido</p>
                  <p className="text-sm text-slate-700">{carga.tipo_veiculo || '-'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Rotas */}
          <div className="mb-8">
            <h3 className="font-semibold text-slate-900 mb-4 text-lg">2. ROTA E PRAZO</h3>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-blue-900">Origem</span>
                </div>
                <p className="text-sm text-slate-700">{carga.origem_cidade} - {carga.origem_estado}</p>
                {carga.origem_endereco && <p className="text-sm text-slate-500">{carga.origem_endereco}</p>}
              </div>

              <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-green-600" />
                  <span className="font-medium text-green-900">Destino</span>
                </div>
                <p className="text-sm text-slate-700">{carga.destino_cidade} - {carga.destino_estado}</p>
                {carga.destino_endereco && <p className="text-sm text-slate-500">{carga.destino_endereco}</p>}
              </div>
            </div>

            <div className="mt-4 bg-slate-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-900">Data de Coleta</p>
                  <p className="text-sm text-slate-700">
                    {carga.data_coleta ? format(new Date(carga.data_coleta), 'dd/MM/yyyy', { locale: ptBR }) : 'A combinar'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Data de Entrega</p>
                  <p className="text-sm text-slate-700">
                    {carga.data_entrega ? format(new Date(carga.data_entrega), 'dd/MM/yyyy', { locale: ptBR }) : 'A combinar'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Distância Estimada</p>
                  <p className="text-sm text-slate-700">{carga.distancia || carga.km || 'Não informada'} km</p>
                </div>
              </div>
            </div>
          </div>

          {/* Valores */}
          <div className="mb-8">
            <h3 className="font-semibold text-slate-900 mb-4 text-lg">3. VALORES E CONDIÇÕES DE PAGAMENTO</h3>

            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Weight className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-slate-600 font-medium">Valor do Frete</span>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-slate-900">
                    {carga.tipo_valor === 'a_combinar' 
                      ? 'A COMBINAR' 
                      : `R$ ${parseFloat(carga.valor_frete || carga.valor_total || carga.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                    }
                  </span>
                  {carga.tipo_valor === 'tonelada' && (
                    <p className="text-xs text-slate-500 mt-1">Frete por tonelada</p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-slate-200 pt-3">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                  <span className="text-sm text-slate-600 font-medium">Taxa de Agenciamento</span>
                </div>
                <div className="text-right">
                  <span className="text-lg font-bold text-amber-700">R$ 50,00</span>
                  <p className="text-xs text-amber-600">Paga via PIX na contratação</p>
                </div>
              </div>
            </div>

            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-2">
                <DollarSign className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Forma de Pagamento do Frete</p>
                  <p className="text-sm text-blue-700 leading-relaxed">
                    O pagamento do frete será efetuado conforme valor total acordado entre as partes. 
                    A taxa de agenciamento de R$ 50,00 é paga separadamente via PIX no momento da 
                    contratação e não interfere no valor do frete acordado.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Responsabilidades */}
          <div className="mb-8">
            <h3 className="font-semibold text-slate-900 mb-4 text-lg">4. RESPONSABILIDADES DO MOTORISTA</h3>

            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <ShieldAlert className="w-5 h-5 text-slate-400 mt-0.5" />
                <p className="text-sm text-slate-700">
                  Manter o veículo em perfeitas condições de funcionamento, com documentação em dia.
                </p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <ShieldAlert className="w-5 h-5 text-slate-400 mt-0.5" />
                <p className="text-sm text-slate-700">
                  Seguir a rota estabelecida e respeitar os prazos de coleta e entrega.
                </p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <ShieldAlert className="w-5 h-5 text-slate-400 mt-0.5" />
                <p className="text-sm text-slate-700">
                  Responsabilizar-se pela integridade da carga durante o transporte.
                </p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <ShieldAlert className="w-5 h-5 text-slate-400 mt-0.5" />
                <p className="text-sm text-slate-700">
                  Comunicar imediatamente qualquer imprevisto ou atraso na entrega.
                </p>
              </div>
            </div>
          </div>

          {/* Declaração de Responsabilidade */}
          {!contratoGerado && (
            <div className="mb-8 print:hidden">
              <h3 className="font-semibold text-slate-900 mb-4 text-lg">5. DECLARAÇÃO DE RESPONSABILIDADE</h3>

              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 space-y-3">
                <p className="text-sm text-slate-700 leading-relaxed">
                  Declaro estar ciente de que o <strong>BoraFrete</strong> atua exclusivamente como plataforma intermediadora, 
                  não se responsabilizando por roubo, extravio, avarias, acidentes ou quaisquer ocorrências durante o transporte.
                </p>

                <p className="text-sm text-slate-700 leading-relaxed">
                  A responsabilidade pela averbação da carga, consulta ao gerenciamento de risco e contratação de seguro 
                  cabe exclusivamente à <strong>empresa contratante</strong>.
                </p>

                <p className="text-sm text-slate-700 leading-relaxed">
                  O motorista declara que o veículo encontra-se em condições adequadas de transporte e que possui 
                  documentação regular para execução do frete.
                </p>

                <p className="text-sm text-slate-700 leading-relaxed">
                  O pagamento do frete será calculado com base no <strong>peso líquido ou volume real</strong> da carga transportada, 
                  não sendo considerado o peso bruto total do veículo.
                </p>

                <p className="text-sm text-slate-700 leading-relaxed">
                  Em caso de sinistro ou qualquer ocorrência operacional, a comunicação deverá ser feita imediatamente 
                  à empresa contratante.
                </p>
              </div>

              <div className="mt-4 flex items-start gap-3">
                <Checkbox 
                  id="termos" 
                  checked={checklist.termos}
                  onCheckedChange={() => handleChecklistChange('termos')}
                />
                <label htmlFor="termos" className="text-sm text-slate-700 cursor-pointer leading-relaxed">
                  Ao aceitar o frete, ambas as partes concordam integralmente com estas condições e assumem 
                  suas respectivas responsabilidades legais e operacionais.
                </label>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-start gap-2">
                  <MessageCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Precisa de seguro?</p>
                    <p className="text-sm text-blue-700 mb-2">
                      Fale com nossa gerenciadora de risco para contratar seguro de carga.
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleAbrirWhatsApp}
                      className="border-green-600 text-green-600 hover:bg-green-50"
                    >
                      <Phone className="w-4 h-4 mr-2" /> WhatsApp da Gerenciadora
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Aceite */}
          {!contratoGerado && (
            <div className="mt-8 pt-8 border-t border-slate-200 print:hidden">
              <div className="flex items-start gap-3 mb-6">
                <Checkbox 
                  id="aceite" 
                  checked={aceito}
                  onCheckedChange={() => setAceito(!aceito)}
                />
                <label htmlFor="aceite" className="text-sm text-slate-700 cursor-pointer">
                  Li e aceito todos os termos e condições deste contrato. Declaro que as informações 
                  fornecidas são verdadeiras e assumo total responsabilidade pelo transporte desta carga.
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleAceitar}
                  disabled={!aceito || !checklist.termos || assinando}
                  className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white font-semibold"
                >
                  {assinando ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Salvando...</>
                  ) : (
                    <><CheckCircle2 className="w-5 h-5 mr-2" /> Assinar Contrato Digitalmente</>
                  )}
                </Button>

                <Button
                  variant="outline"
                  onClick={() => navigate('/buscar-cargas')}
                  className="flex-1 h-12"
                >
                  Cancelar
                </Button>
              </div>

              {!aceito && (
                <p className="text-sm text-amber-600 mt-3 text-center">
                  Você precisa aceitar os termos para continuar
                </p>
              )}

              {!checklist.termos && aceito && (
                <p className="text-sm text-amber-600 mt-3 text-center">
                  Confirme a declaração de responsabilidade acima
                </p>
              )}
            </div>
          )}

          {contratoGerado && (
            <div className="mt-8 pt-8 border-t border-slate-200 print:hidden">
              <div className="bg-green-50 rounded-lg p-6 text-center border border-green-200">
                <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-green-800 mb-2">Contrato Assinado!</h3>
                <p className="text-green-700 mb-4">
                  Este contrato foi aceito em {format(new Date(), 'dd/MM/yyyy HH:mm', { locale: ptBR })}.
                </p>
                <Button 
                  onClick={() => navigate('/painel')}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Ir para Meu Painel
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}